<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from "vue";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import Infotext from "@/components/Infotext.vue";
import { NavvItem } from "@/model/NavItem";
import Steps from "@/components/Steps.vue";
import Zeile from "@/components/grid/Zeile.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zelle from "@/components/grid/Zelle.vue";
import formValidierung from "@/businesslogic/formValidierung";
import JaNeinRadio from "@/components/JaNeinRadio.vue";
import dataUtils from "@/businesslogic/utils/dataUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "BasisdatenHG",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    Ueberschrift,
    Infotext,
    ValidierungsFehler,
    Zeile,
    TitelZelle,
    Zelle,
    Steps,
    JaNeinRadio,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
  },
  setup(props, { emit }) {
    const piniaStore = useFormStore();
    const fehler = ref({} as any);

    const { erzwingeGanzzahl } = formValidierung();

    const { gt } = dataUtils();

    const anzahlWohnungenRequired = computed(() => {
      return (
        !piniaStore.state.gebaeudedaten.unbebautesGruendstueck &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== "0002" &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== "0006"
      );
    });

    const gewerbeVorhandenRequired = computed(() => {
      return (
        !piniaStore.state.gebaeudedaten.unbebautesGruendstueck &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== "0002" &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== "0003" &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== "0006"
      );
    });

    const anzahlStellplaetzeGaragenRequired = computed(() => {
      return (
        piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false &&
        piniaStore.state.gebaeudedaten.gebaeudetyp === "0006"
      );
    });

    const gewerbeVorhanden = () => {
      return (
        piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== "0002" &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== "0003" &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== "0006" &&
        piniaStore.state.gebaeudedaten.gewerbeVorhanden === true
      );
    };

    const gewerbeAnzahlRequired = computed(() => {
      return gewerbeVorhanden();
    });

    const gewerbeflaecheRequired = computed(() => {
      return gewerbeVorhanden();
    });

    const leerstandRequired = computed(() => {
      return (
        piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false &&
        (piniaStore.state.gebaeudedaten.gebaeudetyp === "0001" ||
          piniaStore.state.gebaeudedaten.gebaeudetyp === "0002") &&
        piniaStore.state.gebaeudedaten.gebaeudenutzung === "0005"
      );
    });

    const artDerMiteigentuemerschaftRequired = computed(() => {
      return (
        piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false &&
        piniaStore.state.gebaeudedaten.alleineigentuemer === false
      );
    });

    const wohnungOderGebaude = computed(() => {
      if (piniaStore.state.gebaeudedaten.gebaeudetyp === "0003") {
        return gt(piniaStore.state.gebaeudedaten.anzahlWohnungen, 1)
          ? "Sind die Wohnungen ganz oder teilweise"
          : "Ist die Wohnung ";
      }
      return "Ist das Gebäude ganz oder teilweise";
    });

    const eigentuemerschaftIstWEG = computed(() => {
      return piniaStore.state.gebaeudedaten.miteigentuemerTyp === "WEG";
    });

    const minAnzahlWohnungen = computed(() => {
      return piniaStore.state.gebaeudedaten.gebaeudetyp === "0003" ? 1 : 2;
    });

    const fehlermeldungAnzahlWohnungen = computed(() => {
      return piniaStore.state.gebaeudedaten.gebaeudetyp === "0003"
        ? "Bitte geben Sie eine Wohnungsanzahl zwischen 1 und 999 ein."
        : "Bitte geben Sie eine Wohnungsanzahl zwischen 2 und 999 ein.";
    });

    const stellplaetzeGaragenVersicherbar = computed(() => {
      return piniaStore.state.isMakler && piniaStore.state.gebaeudedaten.miteigentuemerTyp !== "EHE";
    });

    const eingabenSindValide = () => {
      return new Promise((resolve) => {
        const { validiereForm, getFormFehlerAnzahl, getFormFehler } = formValidierung();
        validiereForm("").then(() => {
          fehler.value = getFormFehler();
          resolve(getFormFehlerAnzahl() === 0);
        });
      });
    };

    const nextPage = () => {
      const entferneGewerbeDetails = () => {
        if (piniaStore.state.gebaeudedaten.gewerbeVorhanden === false) {
          piniaStore.state.gebaeudedaten.gewerbeAnzahl = null;
          piniaStore.state.gebaeudedaten.gewerbeflaecheGesamt = null;
        }
      };

      const entferneLeerstandDetails = () => {
        if (piniaStore.state.gebaeudedaten.gebaeudenutzung === "0000") {
          piniaStore.state.gebaeudedaten.leerstand = null;
        }
      };

      eingabenSindValide().then((isValid) => {
        if (isValid) {
          if (piniaStore.state.gebaeudedaten.miteigentuemerTyp === "WEG") {
            piniaStore.state.personendaten.versicherungsnehmer.anrede = "WEG";
          } else if (piniaStore.state.gebaeudedaten.miteigentuemerTyp === "ETG") {
            piniaStore.state.personendaten.versicherungsnehmer.anrede = "ETG";
          } else if (
            piniaStore.state.personendaten.versicherungsnehmer.anrede == "WEG" ||
            piniaStore.state.personendaten.versicherungsnehmer.anrede == "ETG"
          ) {
            //alte Vorbelegung löschen
            piniaStore.state.personendaten.versicherungsnehmer.anrede = "";
          }

          piniaStore.state.gebaeudedaten.gewerbeVorhanden = gewerbeVorhanden();

          entferneGewerbeDetails();
          entferneLeerstandDetails();

          piniaStore.state.neuberechnen = true;
        }
      });
    };

    watch(
      () => [piniaStore.state.berechnungDurchgefuehrt],
      () => {
        if (piniaStore.state.berechnungDurchgefuehrt) {
          piniaStore.state.berechnungDurchgefuehrt = false;
          emit("next-page", { pageIndex: 0 });
        }
      },
    );

    watch(
      () => [piniaStore.state.gebaeudedaten.miteigentuemerTyp],
      () => {
        if (
          piniaStore.state.gebaeudedaten.miteigentuemerTyp === "WEG" &&
          piniaStore.state.gebaeudedaten.gebaeudetyp !== "0004" &&
          piniaStore.state.gebaeudedaten.gebaeudetyp !== "0005"
        ) {
          piniaStore.state.gebaeudedaten.gebaeudetyp = "-1";
        }
        if (
          piniaStore.state.gebaeudedaten.miteigentuemerTyp !== "WEG" &&
          piniaStore.state.gebaeudedaten.gebaeudetyp !== "0001" &&
          piniaStore.state.gebaeudedaten.gebaeudetyp !== "0002" &&
          piniaStore.state.gebaeudedaten.gebaeudetyp !== "0003"
        ) {
          piniaStore.state.gebaeudedaten.gebaeudetyp = "0001";
          piniaStore.state.personendaten.versicherungsnehmer.anrede = "";
        }
      },
    );

    watch(
      () => [piniaStore.state.gebaeudedaten.miteigentuemerTyp],
      () => {
        piniaStore.state.personendaten.verwalterAktiv =
          piniaStore.state.gebaeudedaten.miteigentuemerTyp === "WEG" ||
          piniaStore.state.gebaeudedaten.miteigentuemerTyp === "ETG";
      },
    );

    watch(
      () => [piniaStore.state.gebaeudedaten.alleineigentuemer],
      () => {
        if (piniaStore.state.gebaeudedaten.alleineigentuemer === true) {
          piniaStore.state.gebaeudedaten.miteigentuemerTyp = "";
        }
      },
    );

    watch(
      () => piniaStore.state.gebaeudedaten.unbebautesGruendstueck,
      () => {
        if (piniaStore.state.gebaeudedaten.unbebautesGruendstueck === true) {
          piniaStore.state.gebaeudedaten.miteigentuemerTyp = "";
          piniaStore.state.gebaeudedaten.alleineigentuemer = true;
        }
      },
    );

    watch(
      () => [piniaStore.state.gebaeudedaten.gebaeudetyp],
      () => {
        if (
          piniaStore.state.gebaeudedaten.gebaeudetyp !== "-1" &&
          piniaStore.state.gebaeudedaten.gebaeudetyp !== "0001" &&
          piniaStore.state.gebaeudedaten.gebaeudetyp !== "0004" &&
          piniaStore.state.gebaeudedaten.gebaeudetyp !== "0005"
        ) {
          piniaStore.state.gebaeudedaten.gewerbeVorhanden = false;
          piniaStore.state.gebaeudedaten.gewerbeAnzahl = null;
          piniaStore.state.gebaeudedaten.gewerbeflaecheGesamt = null;
        }
      },
    );

    return {
      piniaStore,
      nextPage,
      fehler,
      props,
      erzwingeGanzzahl,
      leerstandRequired,
      gewerbeflaecheRequired,
      gewerbeAnzahlRequired,
      gewerbeVorhandenRequired,
      anzahlWohnungenRequired,
      wohnungOderGebaude,
      artDerMiteigentuemerschaftRequired,
      eigentuemerschaftIstWEG,
      minAnzahlWohnungen,
      fehlermeldungAnzahlWohnungen,
      stellplaetzeGaragenVersicherbar,
      anzahlStellplaetzeGaragenRequired,
    };
  },
});
</script>

<template>
  <Steps v-bind:items="navItems" :active="0"></Steps>

  <section>
    <ueberschrift titel="Angaben zum Gebäude/Grundstück" />

    <zeile>
      <titel-zelle><strong>Was</strong> möchten Sie versichern?</titel-zelle>
      <zelle class="iconRadioHuG">
        <input
          id="modul1"
          name="modul"
          v-model="piniaStore.state.gebaeudedaten.unbebautesGruendstueck"
          type="radio"
          :value="false"
          required
          tabindex="0"
          autofocus
          class="no-outline"
          aria-required="true"
          aria-label="Gebäude"
          data-fehlermeldung="Bitte wählen Sie, was Sie versichern möchten."
          data-cy="versichertesObjektGebaeude"
        />
        <label class="radiolabel" for="modul1" style="display: inline-block; padding-right: 0"></label>
        <input
          id="modul2"
          name="modul"
          v-model="piniaStore.state.gebaeudedaten.unbebautesGruendstueck"
          type="radio"
          :value="true"
          class="no-outline"
          tabindex="0"
          aria-required="true"
          aria-label="Grundstück"
          data-cy="versichertesObjektGrundstueck"
        />
        <label class="radiolabel" for="modul2" style="display: inline-block; padding-right: 0"></label>
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.gebaeudedaten.unbebautesGruendstueck === true">
      <titel-zelle><strong>Größe</strong> des Grundstücks in m<sup>2</sup></titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="flaecheUnbebaut"
          name="flaecheUnbebaut"
          v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.flaecheUnbebautesGrundstueck"
          class="c-form-control__input"
          min="1"
          max="5000"
          :required="piniaStore.state.gebaeudedaten.unbebautesGruendstueck === true"
          :aria-required="piniaStore.state.gebaeudedaten.unbebautesGruendstueck === true"
          aria-label="Größe des Grundstücks in Quadratmetern"
          data-fehlermeldung="Bitte geben Sie eine Fläche zwischen 1 und 5000 m² ein."
          data-cy="grundstueckFlaeche"
          placeholder="m²"
        />
        <validierungs-fehler :fehler-text="fehler.flaecheUnbebaut" />
        <p class="u-copy-s gev19-infotext--grey">
          Wir versichern unbebaute Grundstücke mit Ausnahme von Fluss-, See- und Meergrundstücken sowie Grundstücken,
          auf denen sich überwiegend Energieerzeugungsanlagen befinden.
        </p>
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false">
      <titel-zelle>Sind Sie Alleineigentümer des Gebäudes?</titel-zelle>
      <zelle>
        <ja-nein-radio v-model="piniaStore.state.gebaeudedaten.alleineigentuemer" name="alleineigentuemer" />
        <validierungs-fehler :fehler-text="fehler.alleineigentuemer" />
      </zelle>
    </zeile>
    <zeile
      v-if="
        piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false &&
        piniaStore.state.gebaeudedaten.alleineigentuemer === false
      "
    >
      <titel-zelle>Art der Miteigentümerschaft</titel-zelle>
      <zelle>
        <select
          name="miteigentuemerTyp"
          v-model="piniaStore.state.gebaeudedaten.miteigentuemerTyp"
          class="c-form-control-select"
          data-cy="miteigentuemerTyp"
          data-fehlermeldung="Bitte wählen Sie eine Art der Miteigentümerschaft aus."
          :required="artDerMiteigentuemerschaftRequired"
          :aria-required="artDerMiteigentuemerschaftRequired"
          aria-label="Art der Miteigentümerschaft"
        >
          <option value="">Bitte wählen</option>
          <option value="EHE">Ehe-/Lebenspartnerschaft</option>
          <option value="ETG">Eigentümergemeinschaft (EG)</option>
          <option value="WEG">Wohnungseigentümergemeinschaft (WEG)</option>
        </select>
        <validierungs-fehler :fehler-text="fehler.miteigentuemerTyp" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false">
      <titel-zelle :zeige-info="true" info-box-id="gebaeudetypInfo"><strong>Nutzungsart</strong></titel-zelle>
      <zelle>
        <select
          id="modul3"
          name="modul"
          v-model="piniaStore.state.gebaeudedaten.gebaeudetyp"
          class="c-form-control-select"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl"
          :required="piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false"
          :aria-required="piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false"
          aria-label="Gebäudetyp"
          data-cy="gebaeudeTyp"
        >
          <option v-if="eigentuemerschaftIstWEG" value="-1">Bitte wählen</option>
          <option v-if="!eigentuemerschaftIstWEG" value="0001">Mehrfamilienhaus</option>
          <option v-if="!eigentuemerschaftIstWEG" value="0002">Einfamilienhaus</option>
          <option v-if="!eigentuemerschaftIstWEG" value="0003">Vermietete Eigentumswohnung</option>
          <option
            v-if="eigentuemerschaftIstWEG && piniaStore.state.gebaeudedaten.alleineigentuemer === false"
            value="0004"
          >
            WEG Gemeinschafts-, Sonder- und Teileigentum
          </option>
          <option
            v-if="eigentuemerschaftIstWEG && piniaStore.state.gebaeudedaten.alleineigentuemer === false"
            value="0005"
          >
            WEG Gemeinschaftseigentum
          </option>
          <option v-if="stellplaetzeGaragenVersicherbar" value="0006">Garage und/oder Stellplätze</option>
        </select>
        <validierungs-fehler :fehler-text="fehler.modul" :reduzierter-abstand="true" />
        <zelle :breite="8">
          <infotext name="gebaeudetypInfo" css-klassen="o-grid__cell--no-space">
            <ul class="u-remove-list-styles">
              <li>
                <strong>Einfamilienhaus</strong><br />
                Das Einfamilienhaus wird ausschließlich zu privaten Zwecken genutzt. Bei einer gewerblichen Nutzung
                wählen Sie bitte die Nutzungsart Mehrfamilienhaus
              </li>
              <li>
                <strong>Mehrfamilienhaus</strong><br />
                Das Mehrfamilienhaus kann sowohl privat als auch gewerblich genutzt sein
              </li>

              <li>
                <strong>WEG Gemeinschafts-, Sonder-, Teileigentum</strong><br />
                Mit dieser Nutzungsart kann sowohl das Gemeinschafts- als auch das Sonder- und Teileigentum einer
                Wohnungseigentümergemeinschaft (WEG) versichert werden
              </li>

              <li>
                <strong>WEG Gemeinschaftseigentum</strong><br />
                Mit dieser Nutzungsart kann das Gemeinschaftseigentum einer Wohnungseigentümergemeinschaft (WEG)
                versichert werden
              </li>

              <li>
                <strong>Vermietete Eigentumswohnung</strong><br />
                Mit dieser Nutzungsart kann das Sondereigentum aus dem Besitz von zu privaten Wohnzwecken vermieteten
                Eigentumswohnungen innerhalb eines Wohngebäudes versichert werden
              </li>

              <li v-if="stellplaetzeGaragenVersicherbar">
                <strong>Garagen und/oder Stellplätze</strong><br />
                Mit dieser Nutzungsart können Garagen und/oder Stellplätze versichert werden
              </li>
              <li>
                <strong>Allgemeiner Hinweis</strong><br />
                Gebäude mit folgender Nutzung können
                <strong>nicht</strong> versichert werden:
                <ul>
                  <li>Zum Abriss bestimmte Gebäude</li>
                  <li>Wohnheime ohne abgeschlossene Wohnungen mit z. B. Gemeinschaftsküchen oder -bädern</li>
                  <li>Gebäude, die gegen den Willen des Eigentümers besetzt sind (Hausbesetzung)</li>
                </ul>
              </li>
            </ul>
          </infotext>
        </zelle>
      </zelle>
    </zeile>
    <zeile
      v-if="
        piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== '0002' &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== '0006'
      "
    >
      <titel-zelle><strong>Anzahl</strong> der Wohnungen</titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="anzahlWohnungen"
          name="anzahlWohnungen"
          v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.anzahlWohnungen"
          class="c-form-control__input"
          :min="minAnzahlWohnungen"
          max="999"
          :required="anzahlWohnungenRequired"
          :aria-required="anzahlWohnungenRequired"
          :data-fehlermeldung="fehlermeldungAnzahlWohnungen"
          aria-label="Anzahl der Wohnungen"
          data-cy="anzahlWohnungen"
        />
        <validierungs-fehler :fehler-text="fehler.anzahlWohnungen" />
      </zelle>
    </zeile>
    <zeile
      v-if="
        piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false &&
        piniaStore.state.gebaeudedaten.gebaeudetyp === '0006'
      "
    >
      <titel-zelle><strong>Anzahl</strong> der Garagen und/oder Stellplätze</titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="anzahlStellplaetzeGaragen"
          name="anzahlStellplaetzeGaragen"
          v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.anzahlStellplaetzeGaragen"
          class="c-form-control__input"
          min="1"
          max="999"
          :required="anzahlStellplaetzeGaragenRequired"
          :aria-required="anzahlStellplaetzeGaragenRequired"
          aria-label="Anzahl der Garagen und/oder Stellplätze"
          data-fehlermeldung="Bitte geben Sie eine Anzahl zwischen 1 und 999 ein."
          data-cy="anzahlStellplaetzeGaragen"
        />
        <validierungs-fehler :fehler-text="fehler.anzahlStellplaetzeGaragen" />
      </zelle>
    </zeile>
    <zeile
      v-if="
        piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== '0002' &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== '0003' &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== '0006'
      "
    >
      <titel-zelle :zeige-info="!piniaStore.state.isMakler" info-box-id="gewerbeInfo">
        Befinden sich <strong>Gewerbebetriebe</strong> im Gebäude?
      </titel-zelle>
      <zelle class="mkRadio">
        <input
          v-model="piniaStore.state.gebaeudedaten.gewerbeVorhanden"
          type="radio"
          id="keinGewerbe"
          name="gewerbe"
          :value="false"
          :required="gewerbeVorhandenRequired"
          :aria-required="gewerbeVorhandenRequired"
          aria-label="Nein, es befinden sich keine Gewerbebetriebe im Gebäude"
          tabindex="0"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl"
          data-cy="gewerbeImGebaeude"
        />
        <label for="keinGewerbe" class="radiolabel">Nein</label>
        <input
          v-model="piniaStore.state.gebaeudedaten.gewerbeVorhanden"
          type="radio"
          id="gewerbe"
          name="gewerbe"
          :value="true"
          aria-label="Ja, es befinden sich Gewerbebetriebe im Gebäude"
          data-cy="noBusinessUse"
        />
        <label for="gewerbe" class="radiolabel">Ja </label>
        <validierungs-fehler :fehler-text="fehler.gewerbe" :reduzierter-abstand="true" />
        <zelle :breite="8">
          <infotext
            name="gewerbeInfo"
            css-klassen="o-grid__cell--no-space"
            text="Wir versichern keine Gebäude, in denen
            grundwassergefährdende Stoffe wie beispielsweise
            Farben, Lacke, Treibstoffe etc. über den
            haushaltsüblichen Umfang hinaus gelagert werden.<br>
            Beispiele: Chemische Reinigungen, Tankstellen,
            Malereibetriebe, Lackierereien, Entsorgungs- und
            Recyclingbetriebe, Herstellerbetriebe von Farben und
            Lacken.<br>
            Wir versichern keine Gebäude in denen sich z. B.
            folgende Gewerbearten befinden: Stundenhotels,
            Bordelle, Discotheken, Nachtclubs und Bars."
          ></infotext>
        </zelle>
        <div v-if="piniaStore.state.isMakler" class="makler-info">
          Wir versichern keine Gebäude, in denen grundwassergefährdende Stoffe wie beispielsweise Farben, Lacke,
          Treibstoffe etc. über den haushaltsüblichen Umfang hinaus gelagert werden.<br />
          Beispiele: Chemische Reinigungen, Tankstellen, Malereibetriebe, Lackierereien, Entsorgungs- und
          Recyclingbetriebe, Herstellerbetriebe von Farben und Lacken.<br /><br />
          Wir versichern keine Gebäude in denen sich z. B. folgende Gewerbearten befinden: Stundenhotels, Bordelle,
          Spielhallen, Wettbüros, Discotheken, Nachtclubs und Bars.
        </div>
      </zelle>
    </zeile>
    <zeile
      v-if="
        piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== '0002' &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== '0003' &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== '0006' &&
        piniaStore.state.gebaeudedaten.gewerbeVorhanden === true
      "
    >
      <titel-zelle><strong>Anzahl</strong> der Gewerbebetriebe</titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="anzahlGewerbebetriebe"
          name="anzahlGewerbebetriebe"
          v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.gewerbeAnzahl"
          class="c-form-control__input"
          min="1"
          data-fehlermeldung="Bitte geben Sie eine Gewerbeanzahl zwischen 1 und 50 ein."
          :required="gewerbeAnzahlRequired"
          :aria-required="gewerbeAnzahlRequired"
          aria-label="Anzahl der Gewerbebetriebe"
          max="50"
          data-cy="anzahlGewerbe"
        />
        <validierungs-fehler :fehler-text="fehler.anzahlGewerbebetriebe" />
      </zelle>
    </zeile>
    <zeile v-if="gewerbeAnzahlRequired">
      <titel-zelle :zeige-info="true" info-box-id="gewerbeflaecheInfo"
        ><strong>Gewerbefläche</strong> in m<sup>2</sup></titel-zelle
      >
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="flaecheGewerbe"
          name="flaecheGewerbe"
          v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.gewerbeflaecheGesamt"
          class="c-form-control__input"
          min="1"
          data-fehlermeldung="
Bitte geben Sie eine Gewerbefläche zwischen 1 und 1000 qm ein."
          :required="gewerbeflaecheRequired"
          :aria-required="gewerbeflaecheRequired"
          aria-label="Gewerbefläche in Quadratmetern"
          max="1000"
          data-cy="flaecheGewerbe"
        />
        <validierungs-fehler :fehler-text="fehler.flaecheGewerbe" />
        <zelle :breite="8">
          <infotext
            name="gewerbeflaecheInfo"
            css-klassen="o-grid__cell--no-space"
            text="Zur Gewerbefläche zählen auch gewerblich genutzte Abstell, Keller- und/oder Bodenräume."
          ></infotext>
        </zelle>
      </zelle>
    </zeile>
    <zeile
      v-if="
        piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== '0006'
      "
    >
      <titel-zelle>
        {{ wohnungOderGebaude }}
        <strong>leerstehend</strong>?
      </titel-zelle>
      <zelle class="mkRadio">
        <input
          v-model="piniaStore.state.gebaeudedaten.gebaeudenutzung"
          type="radio"
          id="ohneLeerstand"
          name="gebaeudenutzung"
          value="0000"
          :required="!piniaStore.state.gebaeudedaten.unbebautesGruendstueck"
          :aria-required="!piniaStore.state.gebaeudedaten.unbebautesGruendstueck"
          aria-label="Kein Leerstand"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl"
          data-cy="UnusedSpaceNo"
        />
        <label for="ohneLeerstand" class="radiolabel">Nein</label>
        <input
          v-model="piniaStore.state.gebaeudedaten.gebaeudenutzung"
          type="radio"
          id="mitLeerstand"
          name="gebaeudenutzung"
          value="0005"
          aria-label="Leerstand"
          data-cy="UnusedSpace"
        />
        <label for="mitLeerstand" class="radiolabel">Ja </label>
        <validierungs-fehler :fehler-text="fehler.gebaeudenutzung" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <zeile
      v-if="
        piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false &&
        piniaStore.state.gebaeudedaten.gebaeudenutzung === '0005'
      "
    >
      <titel-zelle
        v-if="piniaStore.state.gebaeudedaten.gewerbeVorhanden"
        :zeige-info="piniaStore.state.gebaeudedaten.gewerbeVorhanden"
        info-box-id="leerstandInfo"
        ><strong>Leerstand</strong> der Gesamtfläche in %
      </titel-zelle>
      <titel-zelle v-else>Leerstand der <strong>Gesamtwohnfläche</strong> in %</titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="leerstand"
          name="leerstand"
          v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.leerstand"
          class="c-form-control__input"
          data-cy="flaecheLeerstand"
          min="1"
          max="100"
          :required="leerstandRequired"
          :aria-required="leerstandRequired"
          tabindex="0"
          aria-label="Leerstand in Prozent"
          data-fehlermeldung="Bitte geben Sie einen Prozentwert zwischen 1 und 100 ein."
        />
        <validierungs-fehler :fehler-text="fehler.leerstand" />
        <zelle :breite="8">
          <infotext
            v-if="piniaStore.state.gebaeudedaten.gewerbeVorhanden"
            name="leerstandInfo"
            css-klassen="o-grid__cell--no-space"
            text="Zur Gesamtfläche zählen alle Wohn- und Gewerbeflächen."
          ></infotext>
        </zelle>
      </zelle>
    </zeile>
    <zeile>
      <zelle :breite="3" :offset="4">
        <button
          class="c-button c-button--full-width u-mar-t-2 u-mar-b-2 mobil-mehrAbstand"
          @click="nextPage"
          data-cy="submitZumVergleich"
        >
          Weiter zum Vergleich
        </button>
      </zelle>
    </zeile>
  </section>
</template>

<style lang="scss" scoped>
.makler-info {
  color: #6c757d;
}
</style>
